import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_ASPECT_RATIO, UV_OFFSET_X, UV_OFFSET_Y } from '@config/constants.js';

/**
 * CardMesh - Manages the 3D card geometry and materials
 *
 * Responsibilities:
 * - Create card plane geometry with correct aspect ratio
 * - Set up material with texture support
 * - Handle double-sided rendering
 * - Manage card positioning and rotation
 * - Update textures dynamically
 */
export class CardMesh {
  constructor() {
    this.group = null; // Container for both front and back meshes
    this.frontMesh = null;
    this.backMesh = null;
    this.geometry = null;
    this.frontMaterial = null;
    this.backMaterial = null;
    this.frontTexture = null;
    this.backTexture = null;

    this._initialize();
  }

  /**
   * Initialize the card mesh with geometry and material
   * @private
   */
  _initialize() {
    this._createGeometry();
    this._createMaterials();
    this._createMeshes();
  }

  /**
   * Create the rounded box geometry for the card with thickness
   * Uses aspect ratio from constants to maintain card proportions
   * @private
   */
  _createGeometry() {
    // Create a box that maintains the card's aspect ratio
    // Width is normalized to 1, height is calculated from aspect ratio
    const width = 1;
    const height = width / CARD_ASPECT_RATIO;
    const depth = 0.008; // Card thickness (thin but visible)
    const radius = 0.03; // Corner radius for rounded edges

    // Create rounded box geometry for a physical card appearance
    this.geometry = this._createRoundedBoxGeometry(width, height, depth, radius);
  }

  /**
   * Create a rounded box geometry using ExtrudeGeometry
   * @private
   */
  _createRoundedBoxGeometry(width, height, depth, radius) {
    // Create a rounded rectangle shape
    const shape = new THREE.Shape();
    const x = -width / 2;
    const y = -height / 2;

    // Start at top-left corner (after radius)
    shape.moveTo(x + radius, y);

    // Top edge
    shape.lineTo(x + width - radius, y);
    // Top-right corner
    shape.quadraticCurveTo(x + width, y, x + width, y + radius);

    // Right edge
    shape.lineTo(x + width, y + height - radius);
    // Bottom-right corner
    shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);

    // Bottom edge
    shape.lineTo(x + radius, y + height);
    // Bottom-left corner
    shape.quadraticCurveTo(x, y + height, x, y + height - radius);

    // Left edge
    shape.lineTo(x, y + radius);
    // Top-left corner
    shape.quadraticCurveTo(x, y, x + radius, y);

    // Extrude settings
    const extrudeSettings = {
      depth: depth,
      bevelEnabled: false, // Disable bevel for cleaner edges
      curveSegments: 12, // Smooth curves
      UVGenerator: THREE.WorldUVGenerator // Use world UV generator for proper texture mapping
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Center the geometry on the Z axis
    geometry.translate(0, 0, -depth / 2);

    // Fix UV mapping for proper texture alignment
    this._fixUVMapping(geometry, width, height);

    return geometry;
  }

  /**
   * Fix UV mapping for extruded geometry to ensure textures display correctly
   * Only applies to front and back faces, not edges
   * @private
   */
  _fixUVMapping(geometry, width, height) {
    const uvAttribute = geometry.attributes.uv;
    const posAttribute = geometry.attributes.position;
    const normalAttribute = geometry.attributes.normal;

    for (let i = 0; i < uvAttribute.count; i++) {
      const x = posAttribute.getX(i);
      const y = posAttribute.getY(i);
      const z = posAttribute.getZ(i);
      const nz = normalAttribute.getZ(i);

      // Only fix UVs for front and back faces (where normal Z is close to 1 or -1)
      // Skip edge vertices
      if (Math.abs(nz) > 0.9) {
        // Map X and Y positions to UV coordinates (0-1 range)
        // Apply offsets to center the texture properly
        const u = (x + width / 2) / width + UV_OFFSET_X;
        const v = (y + height / 2) / height + UV_OFFSET_Y;

        uvAttribute.setXY(i, u, v);
      }
    }

    uvAttribute.needsUpdate = true;
  }

  /**
   * Create materials for front, back, and edges
   * Uses MeshStandardMaterial for better lighting and realistic appearance
   * @private
   */
  _createMaterials() {
    // Front face material (with texture)
    this.frontMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.3,
      metalness: 0.1,
      transparent: false,
    });

    // Back face material (with texture)
    this.backMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.3,
      metalness: 0.1,
      transparent: false,
    });

    // Edge material (sides of the card) - medium grey color
    this.edgeMaterial = new THREE.MeshStandardMaterial({
      color: 0x6a6a6a, // Medium grey (#6A6A6A)
      roughness: 0.8,
      metalness: 0.0,
    });
  }

  /**
   * Create separate meshes for front and back with edge mesh
   * @private
   */
  _createMeshes() {
    // Create group to hold all meshes
    this.group = new THREE.Group();

    // Create materials array for extruded geometry
    // Index 0: Front/back faces, Index 1: Edges
    const frontMaterials = [this.frontMaterial, this.edgeMaterial];
    const backMaterials = [this.backMaterial, this.edgeMaterial];

    // Create front mesh (faces toward camera)
    this.frontMesh = new THREE.Mesh(this.geometry, frontMaterials);
    this.frontMesh.position.z = 0.0001; // Minimal offset to prevent z-fighting
    this.frontMesh.castShadow = true;
    this.frontMesh.receiveShadow = true;

    // Create back mesh (rotated 180 degrees)
    this.backMesh = new THREE.Mesh(this.geometry, backMaterials);
    this.backMesh.rotation.y = Math.PI; // Flip 180 degrees
    this.backMesh.position.z = -0.0001; // Minimal offset to prevent z-fighting
    this.backMesh.castShadow = true;
    this.backMesh.receiveShadow = true;

    // Add meshes to group
    this.group.add(this.frontMesh);
    this.group.add(this.backMesh);
  }

  /**
   * Set the texture for the card (sets front texture)
   * @param {THREE.Texture} texture - The texture to apply
   */
  setTexture(texture) {
    this.setFrontTexture(texture);
  }

  /**
   * Set the front texture
   * @param {THREE.Texture} texture - The front texture
   */
  setFrontTexture(texture) {
    if (texture) {
      this.frontTexture = texture;
      this.frontMaterial.map = texture;
      this.frontMaterial.needsUpdate = true;
    }
  }

  /**
   * Set the back texture
   * @param {THREE.Texture} texture - The back texture
   */
  setBackTexture(texture) {
    if (texture) {
      this.backTexture = texture;
      this.backMaterial.map = texture;
      this.backMaterial.needsUpdate = true;
    }
  }

  /**
   * Set both front and back textures (useful when they're the same)
   * @param {THREE.Texture} frontTexture - Front texture
   * @param {THREE.Texture} backTexture - Back texture (defaults to front if not provided)
   */
  setTextures(frontTexture, backTexture = null) {
    this.setFrontTexture(frontTexture);
    this.setBackTexture(backTexture || frontTexture);
  }

  /**
   * Flip to show front side (deprecated - both sides are always visible)
   */
  showFront() {
    console.warn('showFront() is deprecated - both sides are always visible');
  }

  /**
   * Flip to show back side (deprecated - both sides are always visible)
   */
  showBack() {
    console.warn('showBack() is deprecated - both sides are always visible');
  }

  /**
   * Set the position of the card
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   */
  setPosition(x, y, z) {
    this.group.position.set(x, y, z);
  }

  /**
   * Set the rotation of the card
   * @param {number} x - Rotation around X axis (radians)
   * @param {number} y - Rotation around Y axis (radians)
   * @param {number} z - Rotation around Z axis (radians)
   */
  setRotation(x, y, z) {
    this.group.rotation.set(x, y, z);
  }

  /**
   * Rotate the card by delta values
   * @param {number} deltaX - Delta rotation around X axis (radians)
   * @param {number} deltaY - Delta rotation around Y axis (radians)
   * @param {number} deltaZ - Delta rotation around Z axis (radians)
   */
  rotate(deltaX, deltaY, deltaZ) {
    this.group.rotation.x += deltaX;
    this.group.rotation.y += deltaY;
    this.group.rotation.z += deltaZ;
  }

  /**
   * Get the Three.js group object (contains front and back meshes)
   * @returns {THREE.Group}
   */
  getMesh() {
    return this.group;
  }

  /**
   * Update material properties for both front and back
   * @param {Object} properties - Material properties to update
   */
  updateMaterial(properties) {
    Object.assign(this.frontMaterial, properties);
    Object.assign(this.backMaterial, properties);
    Object.assign(this.edgeMaterial, properties);
    this.frontMaterial.needsUpdate = true;
    this.backMaterial.needsUpdate = true;
    this.edgeMaterial.needsUpdate = true;
  }

  /**
   * Create a placeholder texture with a pattern
   * Useful for testing before actual textures are loaded
   * @returns {THREE.Texture}
   */
  static createPlaceholderTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = CARD_WIDTH;
    canvas.height = CARD_HEIGHT;
    const ctx = canvas.getContext('2d');

    // Draw a simple pattern
    // Background
    ctx.fillStyle = '#2c3e50';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#ecf0f1';
    ctx.lineWidth = 10;
    ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);

    // Card text
    ctx.fillStyle = '#ecf0f1';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('CARD', canvas.width / 2, canvas.height / 2);
    ctx.font = '24px Arial';
    ctx.fillText('Front', canvas.width / 2, canvas.height / 2 + 40);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    return texture;
  }

  /**
   * Clean up resources
   */
  dispose() {
    // Dispose geometry
    if (this.geometry) {
      this.geometry.dispose();
    }

    // Dispose front material
    if (this.frontMaterial) {
      if (this.frontMaterial.map) {
        this.frontMaterial.map.dispose();
      }
      this.frontMaterial.dispose();
    }

    // Dispose back material
    if (this.backMaterial) {
      if (this.backMaterial.map) {
        this.backMaterial.map.dispose();
      }
      this.backMaterial.dispose();
    }

    // Dispose edge material
    if (this.edgeMaterial) {
      this.edgeMaterial.dispose();
    }

    // Dispose stored textures
    if (this.frontTexture) {
      this.frontTexture.dispose();
    }

    if (this.backTexture) {
      this.backTexture.dispose();
    }

    // Clear references
    this.frontMesh = null;
    this.backMesh = null;
    this.group = null;
    this.geometry = null;
    this.frontMaterial = null;
    this.backMaterial = null;
    this.edgeMaterial = null;
  }
}
